/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import React from 'react';
import type { FieldValues } from 'react-hook-form';

import type { ZodType } from 'zod';

import logo from '../../../../assets/logo/logo--full-vertical.svg';
import crab from '../../../../assets/logo/logo.svg';
import { useStepFlow } from '../../hooks/use-step-flow';
import { createClassName, createStyleHelper } from '../../utils/class-names';
import type { FCWithChildren } from '../../utils/component.interface';
import { FormBuilder } from '../form-builder/form-builder';
import type {
    ChangeEvent,
    IComponent,
    IField,
    IFormBuilderProps,
    IOnFieldChangeParameters,
} from '../form-builder/form-builder';
import { ImageLogo } from '../image-logo/image-logo.';

import styles from './form-flow.module.scss';

const style = createStyleHelper(styles, 'crab-container');

export interface IFlowState<
    OutputType extends FieldValues = any,
    BeforeEnterStepDataValues extends Record<string, any> = Record<string, any>,
> {
    formState: {
        validations?: ChangeEvent<OutputType>['formState'];
        values?: ChangeEvent<OutputType>['values'];
    };
    additionalData: BeforeEnterStepDataValues;
    isLoading: boolean;
}

export interface IStep<
    BeforeEnterStepDataValues extends Record<string, any> = Record<string, any>,
    OutputType extends FieldValues = any,
> {
    name: string;
    schema?: ZodType<OutputType>;
    onBeforeEnterStep?: (formData?: OutputType) => Promise<BeforeEnterStepDataValues>;
    components: (
        state: IFlowState<OutputType, BeforeEnterStepDataValues>,
    ) => Readonly<Array<IField | IComponent | undefined | null>>;
    onFieldChange?: (params: IOnFieldChangeParameters) => void;
    onBeforeLeaveStep?: (
        formData: OutputType | undefined,
        controls?: IBeforeStepControls,
    ) => Promise<void | {
        errors?: Array<{ type: 'field' | 'form'; fieldName?: string; messages?: string[] }>;
        additionalData?: Record<string, unknown>;
    }>;
}

export interface IBeforeStepControls {
    goToStep: (stepName: string) => void;
}

export interface IFormFlowProps {
    className?: string;
    page?: string;
    readonly steps: ReadonlyArray<Readonly<IStep>> | any;
    onFlowComplete?: (formData?: Record<string, any>) => void | Promise<void>;
    resetForm?: (input: Record<string, unknown>) => Record<string, unknown>;
}

export const FormFlow: FCWithChildren<IFormFlowProps> = ({
    className,
    steps,
    page,
    resetForm,
    onFlowComplete,
    children,
}) => {
    const [formState, setFormState] = useState<ChangeEvent>();
    const [formFields, setFormFields] = useState<ReturnType<IStep['components']>>([]);
    const [formFlowFieldValues, setFormFlowFieldValues] = useState<Record<string, any>>({});

    const {
        isLoading,
        stepAdditionalData,
        displayedStep,
        errors,
        goToNextStep,
        setCurrentStep,
        currentStep,
    } = useStepFlow({
        steps,
        formDataProps: formFlowFieldValues,
    });

    const onChangeHandler = useCallback<NonNullable<IFormBuilderProps['onChange']>>(
        (formChange) => {
            setFormState(formChange);
        },
        [setFormState],
    );

    const onSubmitHandler = useCallback(
        async (formData: Record<string, any>) => {
            const allFormData = {
                ...formFlowFieldValues,
                ...formData,
            };

            setFormFlowFieldValues(allFormData);

            const { isLastStep, onLeave } = goToNextStep(allFormData);

            const shouldProceed = await onLeave;

            if (isLastStep && shouldProceed) {
                await onFlowComplete?.(allFormData);
            }
        },
        [formFlowFieldValues, goToNextStep, onFlowComplete],
    );

    useEffect(() => {
        if (!displayedStep) {
            return;
        }

        const allFormData = {
            ...formFlowFieldValues,
            ...formState?.values,
        };

        setFormFields(
            displayedStep.components({
                additionalData: stepAdditionalData,
                formState: {
                    validations: formState?.formState,
                    values: allFormData,
                },
                isLoading,
            }),
        );
    }, [
        setFormFields,
        formFlowFieldValues,
        displayedStep,
        formState,
        stepAdditionalData,
        isLoading,
    ]);

    const allFormData = {
        ...formFlowFieldValues,
        ...formState?.values,
    };

    if (!displayedStep) {
        return <>{children}</>;
    }

    /**
     @todo: Change login ulr with the action from the header
     **/
    const stepsOb = [
        { name: 'step-1', logo: crab },
        { name: 'step-2', logo: crab },
        { name: 'step-3', logo: crab },
    ];

    const onStepClick = (val: any) => {
        setCurrentStep(val);
    };

    const currentStepIndex =
        currentStep?.name === 'step-1' ? 0 : currentStep?.name === 'step-2' ? 1 : 2;

    return (
        <div>
            <div className={style('super-container')}>
                {currentStep?.name !== 'complete' && (
                    <React.Fragment>
                        {page === 'registration' && (
                            <>
                                <ImageLogo className={style('logo')} srcPath={logo} />
                                <div className={style('text-registration-center')}>
                                    Sign up and deposit now to get your offer!
                                </div>
                            </>
                        )}

                        {page === 'registration' && (
                            <div className={style('container')}>
                                {stepsOb.map((step, index) => (
                                    <React.Fragment key={index}>
                                        {index !== 0 && <div className={style('line')}></div>}
                                        <div
                                            className={
                                                currentStep?.name === step.name
                                                    ? style('circle-active')
                                                    : index < currentStepIndex
                                                    ? style('circle-previous')
                                                    : style('circle')
                                            }
                                        >
                                            <div
                                                onClick={() => onStepClick(step.name)}
                                                className={style('text')}
                                            >
                                                {currentStep?.name === step.name ? (
                                                    index + 1
                                                ) : index < currentStepIndex ? (
                                                    <ImageLogo
                                                        className={style('circle-logo')}
                                                        srcPath={step.logo}
                                                    />
                                                ) : (
                                                    index + 1
                                                )}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}
                            </div>
                        )}
                    </React.Fragment>
                )}
            </div>
            {currentStep?.name && currentStep?.name === 'step-3' && (
                <div className={style('confirmation-form')}>
                    <h2 className={style('confirmation-form-h2')}>
                        By submitting this form I confirm:
                    </h2>
                    <ul className={style('confirmation-form-ul')}>
                        <li>That the provided registration information is accurate</li>
                        <li>
                            That I am prohibited from allowing any other person to use or access my
                            account
                        </li>
                        <li>
                            That I am not prohibited from participating in sports wagering by the
                            MLGCA
                        </li>
                        <li>
                            That I may not wager on an event sponsored, organized, or conducted by a
                            particular sports governing body if I am associated with that league
                        </li>
                        <li>
                            Customers who opt-in add their phone number and name to our contact page
                            and are directed to accept the privacy policy which notifies them that
                            their numbers will be used for marketing communication by us and
                            third-party vendors and are told that they can opt-out at any time by
                            replying STOP
                        </li>
                    </ul>
                </div>
            )}
            {/*{currentStep?.name === 'complete' && (*/}
            {/*    <BannerCollection*/}
            {/*        className={style('carousel')}*/}
            {/*        page="live-casino"*/}
            {/*        type="lobby"*/}
            {/*    ></BannerCollection>*/}
            {/*)}*/}
            <FormBuilder
                className={createClassName([className])}
                formData={allFormData}
                resetForm={resetForm}
                schema={displayedStep.schema}
                components={formFields}
                onChange={onChangeHandler}
                onFieldChange={displayedStep.onFieldChange}
                onSubmit={onSubmitHandler}
                errors={errors}
                currStep={currentStep?.name}
            />
        </div>
    );
};
